import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import HumanResourcesHeader from 'routes/HumanResourcesModule/HumanResourcesHome/HumanResourcesHeader';
import {
    useDuplicateHrSimulation,
    useGetHrSimulation,
} from 'routes/HumanResourcesModule/Simulation/helpers/hooks/simulation';
import GVTDataGrid from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/GVTData/GVTDataGrid';
import SynthesisHeaderLoader from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/SynthesisHeader/SynthesisHeaderLoader';
import SynthesisHeader from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/SynthesisHeader/SynthesisHeader';
import { StyBackgroundLessTabulatedChip } from 'routes/HumanResourcesModule/components/ui/StyTabulatedChips';
import {
    SCENARIO,
    RECURRENT,
    NON_RECURRENT,
    PAY_COMPONENTS,
    RESUME,
    RESULT,
    CALCULATION_INFORMATION,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import PayConstants from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import StyContentWrapper from 'routes/HumanResourcesModule/components/ui/StyContentWrapper';
import NotFound from 'components/Errors/NotFound';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import ArrivalHypothesesResume from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/ArrivalResume/ArrivalHypothesesResume';
import { isKeyOf } from 'helpers/types/utils';
import { HR_HOME_PAGE_URL } from 'components/App/components/routes';
import GradeAdvancementResume from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/GradeAdvancementResume/GradeAdvancementResume';
import AgentDataGrid from './Agents/AgentDataGrid';
import GVTResume from './Resume/GVTResume/GVTResume';
import Result from './Result/Result';
import DepartureHypothesesResume from './Resume/DepartureResume/DepartureHypothesesResume';
import PayComponentHypothesesResume from './Resume/PayComponentHypothesesResume/PayComponentHypothesesResume';
import NonRecurrentEnvelopesResume from './Resume/NonRecurrentEnvelopesResume/NonRecurrentEnvelopesResume';
import PayComponentsDataGrid from './PayComponents/PayComponentsDataGrid';
import HypothesesChangesContextProvider from './components/contexts/HypothesesChangesContextProvider';
import EnvelopeMantyStatusHypothesesChangesContextProvider from './Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContextProvider';

import HypothesisTypePerMantyStatus from './Agents/components/HypothesisTypePerMantyStatus/HypothesisTypePerMantyStatus';
import HrCalculationsInformation from './components/HrCalculationsInformation';
import SimulationModal from '../SimulationHome/components/SimulationModal/SimulationModal';
import { HrSimulationDuplicatePayload } from '../helpers/types';
import HypothesisChangeInfoAlert from './components/HypothesisChangeInfoAlert';
import NonRecurrentEnvelopeGrid from './Agents/components/NonRecurrentEnvelope/NonRecurrentEnvelopeGrid';
import { useTranslation } from 'react-i18next';

const TRACK_EVENT_BY_TAB = {
    [RESUME]:
        TRACKABLE_EVENTS.human_resources.simulation.hypothesesSummary.openPage,
    [RESULT]: TRACKABLE_EVENTS.human_resources.simulation.results.openPage,
};

const StyComponentsWrapper = styled(Box, {
    name: 'StyComponentsWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    margin: '2em 0',
});

const StyMarginLessTabulatedChip = styled(StyBackgroundLessTabulatedChip, {
    name: 'StyBackgroundLessTabulatedChip',
})(() => ({
    backgroundColor: 'transparent',
    '&.MuiTabs-root': {
        marginLeft: 0,
    },
    '&.MuiBox-root': {
        paddingLeft: 0,
    },
}));

const LoadingPage = (
    <Box className="page-container dx-viewport">
        <StyContentWrapper>
            <Box sx={{ width: '100%' }}>
                <SynthesisHeaderLoader />
            </Box>
        </StyContentWrapper>
    </Box>
);

export default function SimulationSynthesis(): React.ReactElement {
    const { id = '' } = useParams();
    const [showSimulationModal, setShowSimulationModal] = useState(false);
    const [duplicateMode, setDuplicateMode] = useState(false);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const {
        data: hrSimulation,
        isLoading: isHrSimulationLoading,
        isError: isHrSimulationError,
    } = useGetHrSimulation(parseInt(id, 10));
    const duplicateSimulationMutation = useDuplicateHrSimulation(
        parseInt(id, 10),
    );
    const toggleSimulationModal = useCallback(() => {
        setShowSimulationModal((current) => !current);
    }, []);
    const { t } = useTranslation();

    // if an error occur on the getHrSimulation request
    // the user is redirected to the homepage after 5sc.
    useEffect(() => {
        if (!isHrSimulationLoading && isHrSimulationError) {
            setTimeout(() => {
                setShouldRedirect(true);
            }, 5000);
        }
    }, [isHrSimulationError, isHrSimulationLoading]);

    if (isHrSimulationLoading) {
        return LoadingPage;
    }

    const tabsData = [
        {
            key: SCENARIO,
            labelKey: 'humanResources.simulation.scenario',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <>
                        <StyComponentsWrapper sx={{ gap: '1rem' }}>
                            <HrCalculationsInformation
                                tab={CALCULATION_INFORMATION.scenario}
                            />
                            <PayConstants hrSimulation={hrSimulation} />
                            <GVTDataGrid hrSimulation={hrSimulation} />
                        </StyComponentsWrapper>
                    </>
                ),
        },
        {
            key: RECURRENT,
            labelKey: 'humanResources.simulation.recurrents',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <>
                        <StyComponentsWrapper sx={{ gap: '1rem' }}>
                            <HrCalculationsInformation
                                tab={CALCULATION_INFORMATION.recurrentAgent}
                            />
                            <HypothesesChangesContextProvider>
                                <HypothesisChangeInfoAlert
                                    text={t(
                                        'humanResources.simulation.hypothesisChangeAlert',
                                    )}
                                />
                                <AgentDataGrid
                                    hrSimulation={hrSimulation}
                                    recurrence={RecurrenceEnum.RECURRENT}
                                />
                            </HypothesesChangesContextProvider>
                        </StyComponentsWrapper>
                    </>
                ),
        },
        {
            key: NON_RECURRENT,
            labelKey: 'humanResources.simulation.nonRecurrents',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <>
                        <StyComponentsWrapper sx={{ gap: '1rem' }}>
                            <HrCalculationsInformation
                                tab={CALCULATION_INFORMATION.nonRecurrentAgent}
                            />
                            <HypothesesChangesContextProvider>
                                <EnvelopeMantyStatusHypothesesChangesContextProvider
                                    hrSimulationId={hrSimulation.id}
                                >
                                    <HypothesisTypePerMantyStatus
                                        hrSimulation={hrSimulation}
                                    />
                                    <HypothesisChangeInfoAlert
                                        text={t(
                                            'humanResources.simulation.hypothesisChangeAlert',
                                        )}
                                    />
                                    <NonRecurrentEnvelopeGrid
                                        hrSimulation={hrSimulation}
                                    />
                                    <AgentDataGrid
                                        hrSimulation={hrSimulation}
                                        recurrence={
                                            RecurrenceEnum.NON_RECURRENT
                                        }
                                    />
                                </EnvelopeMantyStatusHypothesesChangesContextProvider>
                            </HypothesesChangesContextProvider>
                        </StyComponentsWrapper>
                    </>
                ),
        },
        {
            key: PAY_COMPONENTS,
            labelKey: 'humanResources.simulation.payComponents.tabTitle',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <>
                        <StyComponentsWrapper>
                            <HrCalculationsInformation
                                tab={CALCULATION_INFORMATION.payComponents}
                            />
                            <HypothesesChangesContextProvider>
                                <HypothesisChangeInfoAlert
                                    text={t(
                                        'humanResources.simulation.hypothesisChangeAlert',
                                    )}
                                />
                                <PayComponentsDataGrid
                                    hrSimulation={hrSimulation}
                                />
                            </HypothesesChangesContextProvider>
                        </StyComponentsWrapper>
                    </>
                ),
        },
        {
            key: RESUME,
            labelKey: 'humanResources.simulation.hypothesisSummary',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <StyComponentsWrapper sx={{ gap: 3 }}>
                        <GVTResume hrSimulation={hrSimulation} />
                        <DepartureHypothesesResume
                            hrSimulation={hrSimulation}
                        />
                        <ArrivalHypothesesResume hrSimulation={hrSimulation} />
                        <NonRecurrentEnvelopesResume
                            hrSimulation={hrSimulation}
                        />
                        <PayComponentHypothesesResume
                            hrSimulation={hrSimulation}
                        />
                        <GradeAdvancementResume hrSimulation={hrSimulation} />
                    </StyComponentsWrapper>
                ),
        },
        {
            key: RESULT,
            labelKey: 'humanResources.simulation.resultsSimulation',
            disabled: false,
            displayed: true,
            renderContent: () =>
                hrSimulation && (
                    <StyComponentsWrapper>
                        <Result hrSimulation={hrSimulation} />
                    </StyComponentsWrapper>
                ),
        },
    ];
    const { isPending: isDuplicateLoading, isError } =
        duplicateSimulationMutation;

    // Ensuite, mappez ce tableau pour générer les onglets.
    const tabsList = tabsData
        .filter((tab) => tab.displayed)
        .map((tab) => ({
            label: t(tab.labelKey),
            children: tab.renderContent(),
            pathname: `${HR_HOME_PAGE_URL}/simulation/${id}/${tab.key}`,
            disabled: tab.disabled,
            key: tab.key,
        }));
    const onSave = ({
        recurrentInitialisationStartDate,
        recurrentInitialisationEndDate,
        title,
        orgChartHrsSoftwareIds,
        isEtablissementsFilterActive,
        etablissementSoftwareIds,
        type,
        excludedAgentDepartureHypotheses,
        excludedAgentArrivalHypotheses,
        isPayBudgetFilterActive,
        payBudgetSoftwareIds,
    }: HrSimulationDuplicatePayload) => {
        duplicateSimulationMutation.mutate({
            title,
            orgChartHrsSoftwareIds,
            isEtablissementsFilterActive,
            etablissementSoftwareIds,
            recurrentInitialisationStartDate,
            recurrentInitialisationEndDate,
            type,
            excludedAgentDepartureHypotheses,
            excludedAgentArrivalHypotheses,
            isPayBudgetFilterActive,
            payBudgetSoftwareIds,
        });
    };
    return (
        <>
            <Box
                className="page-container"
                sx={{ display: 'flex', flexDirection: 'column' }}
            >
                <Box sx={{ flex: 0 }}>
                    <HumanResourcesHeader title={t('header.simulations')} />
                </Box>
                <StyContentWrapper>
                    <Box sx={{ width: '100%' }}>
                        {isHrSimulationLoading ? (
                            <SynthesisHeaderLoader />
                        ) : (
                            <>
                                {isHrSimulationError ? (
                                    <>
                                        {shouldRedirect ? (
                                            <Navigate
                                                to="/human_resources/simulation_home"
                                                replace
                                            />
                                        ) : (
                                            <NotFound />
                                        )}
                                    </>
                                ) : (
                                    hrSimulation && (
                                        <>
                                            <SynthesisHeader
                                                hrSimulation={hrSimulation}
                                                setShowSimulationModal={
                                                    setShowSimulationModal
                                                }
                                                setDuplicateMode={
                                                    setDuplicateMode
                                                }
                                            />
                                            <Box sx={{ width: '100%' }}>
                                                <StyMarginLessTabulatedChip
                                                    tabsList={tabsList}
                                                    onTabClicked={(tab) => {
                                                        if (
                                                            !isKeyOf(
                                                                TRACK_EVENT_BY_TAB,
                                                                tab.key,
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        if (
                                                            tab.key !== RESULT
                                                        ) {
                                                            trackEventPRH(
                                                                TRACK_EVENT_BY_TAB[
                                                                    tab.key
                                                                ],
                                                            );
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </>
                                    )
                                )}
                            </>
                        )}
                    </Box>
                </StyContentWrapper>
            </Box>
            {showSimulationModal && (
                <SimulationModal
                    mainTitle={
                        duplicateMode
                            ? t(
                                  'humanResources.simulation.duplicateSimulation.title',
                              )
                            : t('humanResources.simulation.addingSimulation')
                    }
                    creationSubtitle={
                        duplicateMode
                            ? t(
                                  'humanResources.simulation.duplicateSimulation.firstStep.subtitle',
                              )
                            : ''
                    }
                    creationDescription={
                        duplicateMode
                            ? t(
                                  'humanResources.simulation.duplicateSimulation.firstStep.description',
                              )
                            : ''
                    }
                    actionTitle={
                        duplicateMode ? t('common.duplicate') : t('common.save')
                    }
                    onSave={onSave}
                    show={showSimulationModal}
                    onClose={toggleSimulationModal}
                    hrSimulation={duplicateMode ? hrSimulation : undefined}
                    isLoading={isDuplicateLoading}
                    isError={isError}
                    shouldRedirect
                />
            )}
        </>
    );
}
